import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
export function EconomyInnovationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const stats = [{
    value: '$90B+',
    label: 'GDP Contribution'
  }, {
    value: '60%',
    label: "Kenya's GDP"
  }, {
    value: '200+',
    label: 'Tech Startups'
  }, {
    value: '4.8M+',
    label: 'Population'
  }];
  const sectors = [{
    title: 'Technology & Startups',
    description: 'Over 200 tech startups call Nairobi home, earning it the nickname "Silicon Savannah." M-Pesa revolutionized mobile money globally from here.',
    stats: ['200+ Startups', '4 Unicorns', '$2B+ Funding']
  }, {
    title: 'Finance & Banking',
    description: "The Nairobi Securities Exchange is one of Africa's largest stock markets. Major banks and financial institutions have regional headquarters here.",
    stats: ['60+ Banks', 'NSE Listed', 'Regional Hub']
  }, {
    title: 'Real Estate & Construction',
    description: 'Rapid urbanization drives a booming construction sector. Modern skyscrapers and residential developments reshape the skyline constantly.',
    stats: ['$5B+ Projects', '15% Growth', 'Urban Expansion']
  }, {
    title: 'Tourism & Hospitality',
    description: "As East Africa's gateway, Nairobi welcomes millions of visitors annually. Hotels, restaurants, and tour operators form a vital economic pillar.",
    stats: ['2M+ Visitors', '$1.5B Revenue', '100K+ Jobs']
  }];
  const innovations = [{
    title: 'M-Pesa',
    description: 'Mobile money platform that revolutionized financial services in Africa, now used by 50+ million people across 7 countries.',
    impact: 'Financial Inclusion'
  }, {
    title: 'iHub',
    description: "Africa's first tech innovation hub, launched in 2010, has incubated hundreds of startups and fostered the tech ecosystem.",
    impact: 'Startup Ecosystem'
  }, {
    title: 'Nairobi Garage',
    description: 'Co-working space and startup incubator supporting entrepreneurs with mentorship, funding connections, and community.',
    impact: 'Entrepreneurship'
  }, {
    title: 'BRCK',
    description: "Rugged internet connectivity device designed in Nairobi to solve Africa's connectivity challenges.",
    impact: 'Connectivity Solutions'
  }];
  return <div className="min-h-screen bg-white" data-id="element-672">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-673">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-32" data-id="element-674">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-675">
            <h1 className="text-5xl md:text-7xl font-semibold text-charcoal mb-6 leading-tight" data-id="element-676">
              Economy & Innovation
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-677">
              Where African entrepreneurship meets global innovation. Discover
              the economic engine driving East Africa forward.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-id="element-678">
        {/* Stats */}
        <section className="mb-20" data-id="element-679">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-680">
            {stats.map((stat, index) => <motion.div key={stat.label} className="text-center p-6 border border-gray-200" initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: index * 0.1
          }} data-id="element-681">
                <div className="text-3xl font-semibold text-charcoal mb-1" data-id="element-682">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600" data-id="element-683">{stat.label}</div>
              </motion.div>)}
          </div>
        </section>

        {/* Introduction */}
        <section className="mb-20" data-id="element-684">
          <div className="max-w-3xl" data-id="element-685">
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-686">
              Nairobi is East Africa's undisputed economic powerhouse,
              contributing over 60% of Kenya's GDP despite occupying less than
              1% of its land area. The city has transformed from a colonial
              railway depot into a thriving hub of innovation, finance, and
              entrepreneurship. Dubbed the "Silicon Savannah," Nairobi leads
              Africa in tech startup creation, mobile money innovation, and
              venture capital investment.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-687">
              The Nairobi Securities Exchange facilitates billions in daily
              trades. International corporations maintain regional headquarters
              here. Tech hubs like iHub and Nairobi Garage incubate the next
              generation of African unicorns.
            </p>
          </div>
        </section>

        {/* Key Sectors */}
        <section className="mb-20" data-id="element-688">
          <h2 className="text-2xl font-semibold text-charcoal mb-8" data-id="element-689">
            Key Economic Sectors
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-690">
            {sectors.map((sector, index) => <motion.div key={sector.title} className="border border-gray-200 p-8" initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-691">
                <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-692">
                  {sector.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-693">
                  {sector.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-694">
                  {sector.stats.map(stat => <span key={stat} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium" data-id="element-695">
                      {stat}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Innovations */}
        <section data-id="element-696">
          <h2 className="text-2xl font-semibold text-charcoal mb-8" data-id="element-697">
            Nairobi-Born Innovations
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-698">
            {innovations.map((innovation, index) => <motion.div key={innovation.title} className="border border-gray-200 p-6" initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-699">
                <div className="flex items-start justify-between mb-3" data-id="element-700">
                  <h3 className="text-lg font-semibold text-charcoal" data-id="element-701">
                    {innovation.title}
                  </h3>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 font-medium" data-id="element-702">
                    {innovation.impact}
                  </span>
                </div>
                <p className="text-gray-700 leading-relaxed" data-id="element-703">
                  {innovation.description}
                </p>
              </motion.div>)}
          </div>
        </section>
      </div>

      <ScrollToTop data-id="element-704" />
      <Footer data-id="element-705" />
    </div>;
}